import { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { CancelFlowAuthFallback } from '@/features/cancel-subscription/components/cancel-flow-auth-fallback'
import { DiscoveryPage } from '@/pages/discovery'
import { PlanRouteFallback } from '@/pages/plan-route-fallback'
import { UnsubscribeFeedbackRedirect } from '@/shared/components/layout/guards/cancellation-flow-guards'
import {
  ProtectedRoute,
  PublicRoute,
} from '@/shared/components/layout/guards/index'
import {
  AutoLoginPage,
  BillingHistoryPage,
  CancelSubscriptionPage,
  ChangePasswordPage,
  CollectionPage,
  DayPage,
  DayPracticePage,
  DevDeckPage,
  DevWheelPage,
  EmailCancellationPage,
  FlowPracticePage,
  ForgotPasswordPage,
  LanguagePage,
  LoginPage,
  MembershipInfoPage,
  OnboardingPage,
  PauseSubscriptionPage,
  PhotoAlbumPage,
  PlanPage,
  ProfilePage,
  RenewSubscriptionPage,
  ReturnPage,
  SettingsPage,
  StepPracticePreviewPage,
  TodayPage,
  UpdatePaymentMethodPage,
} from '@/shared/components/layout/lazy-pages'
import { ProtectedLayout } from '@/shared/components/layout/protected-layout'
import {
  ContactSupportSuspense,
  PageSpinner,
} from '@/shared/components/layout/suspense-wrappers'

export function AppRoutes() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/" element={<Navigate to="/onboarding" replace />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route element={<ProtectedLayout />}>
          <Route
            path="/plan"
            element={
              <Suspense fallback={<PlanRouteFallback />}>
                <PlanPage />
              </Suspense>
            }
          />
          <Route path="/today" element={<TodayPage />}>
            <Route path="practice/:practiceId" element={<FlowPracticePage />} />
          </Route>
          <Route path="/discovery" element={<DiscoveryPage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route
            path="/profile"
            element={
              <Suspense fallback={<PageSpinner />}>
                <ProfilePage />
              </Suspense>
            }
          />
          <Route
            path="/profile/language"
            element={
              <Suspense fallback={<PageSpinner />}>
                <LanguagePage />
              </Suspense>
            }
          />
          <Route
            path="/profile/settings"
            element={
              <Suspense fallback={<PageSpinner />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="/profile/change-password"
            element={
              <Suspense fallback={<PageSpinner />}>
                <ChangePasswordPage />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        {/* Full-surface takeover, so it sits outside the tabbed layout whose
            onboarding redirect would otherwise pull it off screen. */}
        <Route
          path="/return"
          element={
            <ProtectedRoute>
              <ReturnPage />
            </ProtectedRoute>
          }
        />
        {/* A full-screen album, over no tab bar. */}
        <Route
          path="/profile/album"
          element={
            <ProtectedRoute>
              <PhotoAlbumPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plan/day/:dayId"
          element={
            <ProtectedRoute>
              <DayPage />
            </ProtectedRoute>
          }
        >
          <Route path="practice/:groupId" element={<DayPracticePage />} />
          <Route
            path="flow-practice/:practiceId"
            element={<FlowPracticePage />}
          />
          <Route
            path="preview/:practiceId"
            element={<StepPracticePreviewPage />}
          />
        </Route>
        {/* Auto-login via AppsFlyer OneLink (also used as the SSO handoff
            landing page from mobile webviews — see auto-login.tsx redirect param) */}
        <Route path="/autologin" element={<AutoLoginPage />} />
        {/* Public landing for the Non-Visa cancellation-confirmation email
            link. Unguarded: the user may be logged out; authorisation is the
            one-time token in the URL. No token → the page redirects to
            /membership-info (can't cancel from a manually typed URL). */}
        <Route path="/email-cancellation" element={<EmailCancellationPage />} />
        {/* Subscription management pages.
            These used to be public (legacy: opened via emailed token URL on
            tryleaply.com landings). They now require an authenticated session;
            mobile apps reach them through /autologin?redirect=/membership-info. */}
        <Route
          path="/membership-info"
          element={
            <ProtectedRoute>
              <div className="h-dvh">
                <MembershipInfoPage />
              </div>
            </ProtectedRoute>
          }
        />
        {/* Retired cancel flow URL, still deep-linked by native apps and old
            emails — redirects to the cancel flow below, which guards auth. */}
        <Route
          path="/unsubscribe-feedback/:subscriptionId"
          element={<UnsubscribeFeedbackRedirect />}
        />
        <Route
          path="/pause-subscription"
          element={
            <ProtectedRoute>
              <PauseSubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-payment-method"
          element={
            <ProtectedRoute>
              <UpdatePaymentMethodPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/renew-subscription"
          element={
            <ProtectedRoute>
              <RenewSubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing-history/:subscriptionId"
          element={
            <ProtectedRoute>
              <BillingHistoryPage />
            </ProtectedRoute>
          }
        />
        {/* Cancel flow — single orchestrator hosting a typed step machine.
            The active step is driven by ?step= (default 'intro'); the
            orchestrator owns the chrome, data, and all transitions. */}
        <Route
          path="/cancel-subscription/:subscriptionId"
          element={
            <ProtectedRoute fallback={<CancelFlowAuthFallback />}>
              <CancelSubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route path="/contact-support" element={<ContactSupportSuspense />} />
        {/* Every card-game state without a login, for previews and QA. */}
        {DevDeckPage && <Route path="/dev/deck" element={<DevDeckPage />} />}
        {DevWheelPage && <Route path="/dev/wheel" element={<DevWheelPage />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
